import { useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlertStore } from "@/stores/alertStore";
import { hideGlobalModal, showGlobalModal } from "@/stores/modalStore";

interface DetailPageConfig {
  updateItem: (id: string, data: any) => Promise<{ success: boolean; message?: string }>;
  deleteItem: (id: string) => Promise<{ success: boolean; message?: string }>;
  itemName: string;
  tablePath: string;
  itemDisplayName?: string;
}

export const useDetailPage = (config: DetailPageConfig) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const showAlert = useAlertStore(state => state.showAlert);

  const refImage = useRef<File | null>(null);
  const { updateItem, deleteItem, itemName, tablePath, itemDisplayName } = config;
  const [isFetching, setIsFetching] = useState(false);

  const handleDelete = useCallback(() => {
    showGlobalModal({
      title: `¿Estás seguro de eliminar este ${itemName}?`,
      message: `Estás a punto de eliminar un nuevo ${itemName}. Una vez eliminado, no podrás acceder a el en la librería de ${itemName}s.`,
      iconType: 'warning',
      primaryAction: {
        label: 'Eliminar',
        onClick: () => {
          void (async () => {
            if (!id) return;
            setIsFetching(true);
            const result = await deleteItem(id);
            if (result.success) {
              showAlert({
                text: `${itemName} eliminado exitosamente`,
                message: `El ${itemName} "${itemDisplayName || id}" ha sido eliminado`,
                status: 'success',
              });
              void navigate(tablePath);
            } else {
              showAlert({
                text: `Error al eliminar ${itemName}`,
                message: result.message,
                status: 'error',
              });
            }
            setIsFetching(false);
          })();
        },
        variant: 'primary',
      },
      secondaryAction: {
        label: 'Cancelar',
        onClick: () => {
          hideGlobalModal();
        },
      },
    });
  }, [id, deleteItem, itemDisplayName, itemName, tablePath, showAlert, navigate]);

  const onSubmit = useCallback((data: any) => {
    const { created_at, ...updateData } = data;
    showGlobalModal({
      title: `¿Estas seguro de editar este ${itemName}?`,
      message: `Estás a punto de editar el ${itemName}. Una vez editado, se perderá la configuración o datos anteriores.`,
      iconType: 'warning',
      primaryAction: {
        label: 'Guardar',
        onClick: () => {
          void (async () => {
            if (!id) return;
            setIsFetching(true);
            const result = await updateItem(id, { ...updateData, image: refImage.current });
            if (result.success) {
              showAlert({
                text: `${itemName} actualizado exitosamente`,
                message: `El ${itemName} "${itemDisplayName || id}" ha sido actualizado`,
                status: 'success',
              });
              void navigate(tablePath);
            } else {
              showAlert({
                text: `Error al actualizar ${itemName}`,
                message: result.message,
                status: 'error',
              });
            }
            setIsFetching(false);
          })();
        },
        variant: 'primary',
      },
      secondaryAction: {
        label: 'Cancelar',
        onClick: () => {
          hideGlobalModal();
        },
      },
    });
  }, [id, updateItem, itemDisplayName, itemName, tablePath, showAlert, navigate]);

  return {
    handleDelete,
    onSubmit,
    isFetching,
    refImage,
  };
};
